// Flow types automatically removed

import { createRandomId, getVariableAspectRatio } from './utils.ts';
import { Component, createRef } from 'react';
import { FormattedMessage } from 'dibs-react-intl';
import DropZone from 'react-dropzone';
import classNames from 'classnames';
import { VimeoPlayer } from './VimeoPlayer.jsx';
import { EditPaneImageRenderer } from './EditPaneImageRenderer.jsx';
import { DropZoneOverlay } from './DropZoneOverlay.jsx';
import { CharacterCountTextArea } from './CharacterCountTextArea.jsx';
import { DeleteConfirmation } from './DeleteConfirmation.tsx';
import { ImageEditModal } from './ImageEditModal.tsx';
import { Link } from 'dibs-elements/exports/Link.tsx';
import RotateButton from 'dibs-icons/exports/legacy/ArrowClockwise.ts';
import Lock from 'dibs-icons/exports/legacy/Lock.ts';
import Download from 'dibs-icons/exports/legacy/Download.ts';
import Trash from 'dibs-icons/exports/legacy/TrashCan.ts';
import Pencil from 'dibs-icons/exports/legacy/Pen.ts';
import styles from './styles/EditPane.scss';
const imageEditReqs = {
    aspect: 2 / 3,
    cropShape: 'rect',
    targetWidth: 900,
    targetHeight: 600,
};
const DEFAULT_MAX_CHARS = 440;

class EditPane extends Component {
    constructor(props) {
        super(props);
        this.state = {
            rotation: props.rotation,
            showDeleteConfirmation: false,
            editModalIsOpen: false,
            editPaneHeight: 0,
            editPaneWidth: 0,
        };
        this.rotate = this.rotate.bind(this);
        this.onImgLoad = this.onImgLoad.bind(this);
        this.onCaptionBlur = this.onCaptionBlur.bind(this);
        this.confirmDelete = this.confirmDelete.bind(this);
        this.viewPaneRef = createRef();
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        const { rotation: newRotation } = nextProps;
        const { rotation: oldRotation, onRotationChange } = this.props;
        const currentHeight = this?.viewPaneRef?.current?.offsetHeight;
        const currentWidth = this?.viewPaneRef?.current?.offsetWidth;
        const dimensionsChanged =
            currentHeight !== this.state.viewPaneHeight ||
            currentWidth !== this.state.viewPaneWidth;

        if (newRotation !== oldRotation) {
            this.setState({
                rotation: newRotation,
            });

            if (typeof onRotationChange === 'function') {
                onRotationChange(newRotation);
            }
        }

        if (dimensionsChanged) {
            this.setState({
                viewPaneHeight: this.viewPaneRef.current.offsetHeight,
                viewPaneWidth: this.viewPaneRef.current.offsetWidth,
            });
        }
    }

    componentDidMount() {
        if (this.viewPaneRef.current) {
            this.setState({
                viewPaneHeight: this.viewPaneRef.current.offsetHeight,
                viewPaneWidth: this.viewPaneRef.current.offsetWidth,
            });
        }
    }

    rotate() {
        const { isPending, isRotating, onRotationChange } = this.props;

        if (isPending || isRotating) {
            return null;
        }

        const newRotation = (this.state.rotation + 90) % 360;
        this.setState({
            rotation: newRotation,
        });
        return onRotationChange ? onRotationChange(newRotation) : null;
    }

    onImgLoad() {
        this.setState({
            rotation: 0,
        });
    }

    onCaptionBlur(e) {
        const { onCaptionChange } = this.props;

        if (onCaptionChange) {
            onCaptionChange(e.target.value);
        }
    }

    toggleDeleteConfirmation(showDeleteConfirmation) {
        this.setState({
            showDeleteConfirmation,
        });
    }

    confirmDelete() {
        const { onDelete } = this.props;
        this.toggleDeleteConfirmation(false);
        onDelete();
    }

    render() {
        const {
            isPending,
            isRotating,
            maxCaptionChars,
            media,
            showCaption,
            allowRotation,
            dropZoneAccept,
            isLocked,
            lockCopy,
            allowDownload,
            allowEdit,
            onEdit,
            onDownload,
            onDrop,
            overlayVisible,
            onDragEnter,
            onDragLeave,
            enableReplace,
            requireDeleteConfirmation,
            segmentedView,
            showButtonText,
            showDeleteButton,
            selectedIndex,
            editField,
            enableImageRotation,
        } = this.props;
        const { mediaType, mediaLocation, caption, filename, dimension, mediaId, parent } =
            media || {};

        if (dimension) {
            imageEditReqs.aspect = getVariableAspectRatio(dimension);
            imageEditReqs.targetHeight = imageEditReqs.targetWidth / imageEditReqs.aspect;
        }

        const { rotation, showDeleteConfirmation } = this.state;
        const disabled = isPending || isRotating;
        const onTrashClick = requireDeleteConfirmation
            ? () => this.toggleDeleteConfirmation(true)
            : () => this.props.onDelete();
        return (
            <div
                className={classNames(styles.editingPane, {
                    [styles.editingPaneSegmented]: segmentedView,
                })}
            >
                {showDeleteConfirmation ? (
                    <DeleteConfirmation
                        imageClasses={styles.confirmDelete}
                        onConfirmDelete={this.confirmDelete}
                        onHideDeleteConfirmation={() => this.toggleDeleteConfirmation(false)}
                        segmentedView={segmentedView}
                    />
                ) : (
                    <>
                        <DropZone
                            disabled={!enableReplace}
                            accept={dropZoneAccept}
                            onDrop={onDrop}
                            onDragEnter={onDragEnter}
                            onDragLeave={onDragLeave}
                            noClick
                        >
                            {({ getRootProps, getInputProps }) => (
                                <div {...getRootProps()} className={styles.dropZone}>
                                    <input data-tn="edit-pane-upload-input" {...getInputProps()} />
                                    <DropZoneOverlay visible={overlayVisible} />
                                    <div
                                        className={classNames(styles.viewPane, {
                                            [styles.viewPaneSegmented]: segmentedView,
                                        })}
                                        ref={this.viewPaneRef}
                                    >
                                        {mediaType === 'photo' && (
                                            <EditPaneImageRenderer
                                                containerHeight={this.state.viewPaneHeight}
                                                containerWidth={this.state.viewPaneWidth}
                                                imageSrc={mediaLocation}
                                                onImgLoad={this.onImgLoad}
                                                rotation={rotation}
                                            />
                                        )}
                                        {mediaType === 'video' && (
                                            <VimeoPlayer url={mediaLocation} />
                                        )}
                                    </div>
                                    {showCaption && (
                                        <div className={styles.captionPane}>
                                            <CharacterCountTextArea
                                                onBlur={this.onCaptionBlur}
                                                value={caption || ''}
                                                id="edit-pane-caption-input"
                                                dataTn="edit-pane-caption-input"
                                                disabled={disabled}
                                                max={maxCaptionChars || DEFAULT_MAX_CHARS}
                                                restrictLength
                                            />
                                        </div>
                                    )}
                                    {(isLocked || allowRotation || allowDownload || allowEdit) && (
                                        <div
                                            className={classNames(styles.editBar, {
                                                [styles.editBarSegmenteed]: segmentedView,
                                            })}
                                        >
                                            {isLocked && <Lock className={styles.editPaneLock} />}
                                            {isLocked && (
                                                <span className={styles.editPaneLockCopy}>
                                                    {lockCopy}
                                                </span>
                                            )}
                                            {allowDownload && (
                                                <span
                                                    onClick={onDownload}
                                                    onKeyDown={onDownload}
                                                    role="button"
                                                    tabIndex={0}
                                                >
                                                    <Download
                                                        className={classNames(
                                                            styles.editPaneDownload,
                                                            {
                                                                [styles.editPaneDownloadSegmented]:
                                                                    segmentedView,
                                                            }
                                                        )}
                                                    />
                                                </span>
                                            )}
                                            {allowEdit && mediaType === 'photo' && (
                                                <Link
                                                    onClick={() =>
                                                        this.setState({
                                                            editModalIsOpen: true,
                                                        })
                                                    }
                                                    className={styles.editWrapper}
                                                >
                                                    <Pencil className={styles.editButton} />
                                                    {showButtonText && (
                                                        <FormattedMessage
                                                            id="dibs-media-uploader.EditPane.edit"
                                                            defaultMessage="Edit"
                                                        />
                                                    )}
                                                </Link>
                                            )}
                                            {allowRotation && mediaType === 'photo' && (
                                                <span
                                                    onClick={this.rotate}
                                                    onKeyDown={this.rotate}
                                                    role="button"
                                                    tabIndex={0}
                                                >
                                                    <RotateButton
                                                        dataTn="rotate-button"
                                                        className={
                                                            disabled
                                                                ? styles.rotateButtonDisabled
                                                                : styles.rotateButton
                                                        }
                                                    />
                                                </span>
                                            )}
                                            {showDeleteButton && !isLocked && mediaLocation && (
                                                <Link
                                                    dataTn="delete-image"
                                                    onClick={!isPending ? onTrashClick : undefined}
                                                    className={styles.trashIconWrapper}
                                                >
                                                    <Trash
                                                        className={classNames(
                                                            {
                                                                [styles.trashIconPending]:
                                                                    isPending,
                                                                [styles.trashIcon]: !isPending,
                                                            },
                                                            styles.icon
                                                        )}
                                                    />
                                                    {showButtonText && (
                                                        <FormattedMessage
                                                            id="dibs-media-uploader.EditPane.delete"
                                                            defaultMessage="Delete"
                                                        />
                                                    )}
                                                </Link>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </DropZone>
                        {allowEdit && (
                            <ImageEditModal
                                isOpen={this.state.editModalIsOpen}
                                onClose={() =>
                                    this.setState({
                                        editModalIsOpen: false,
                                    })
                                }
                                onSave={({ crop, degreesRotation, resize }) =>
                                    onEdit({
                                        crop,
                                        field: editField,
                                        filename,
                                        pendingId: createRandomId(),
                                        photoId: parent ? parent.id : mediaId,
                                        resize,
                                        rotation: degreesRotation,
                                        index: selectedIndex,
                                    })
                                }
                                onDelete={onTrashClick}
                                imageUrl={mediaLocation}
                                imageReqs={imageEditReqs}
                                customStyles={styles.editModalCustomStyles}
                                changesRequired={false}
                                enableImageRotation={enableImageRotation}
                                showOnboardingCopy
                                showConfirmationBeforeClose
                            />
                        )}
                    </>
                )}
            </div>
        );
    }
}

export { EditPane };
